import { useState, useEffect } from "react";
import "@/App.css";
import { Clock, Frown, Sparkles, ArrowRight, Menu, X } from "lucide-react";

const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSetOwqcXoSHuboVlFEyZ0A1b0z0O73nioFWmDqMwAzw9PRDrw/viewform";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="font-cabinet text-xl font-black tracking-tight text-white">
            PETCONCEPT
          </a>

          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-btn"
            className="hidden md:flex items-center gap-2 px-5 py-2.5 border border-white/20 rounded-full text-sm font-medium text-white hover:bg-white/10 transition-all duration-300"
          >
            Participer au projet
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-white/10">
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-btn"
              className="block w-full text-center px-5 py-3 border border-white/20 rounded-full text-sm font-medium text-white hover:bg-white/10 transition-all"
            >
              Participer au projet
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section - Emotional hook
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#080808] to-[#050505]" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 text-center">
        <div className="space-y-8">
          <div className="opacity-0 animate-fade-in-up">
            <span className="uppercase text-xs tracking-[0.2em] text-amber-500/80 font-semibold">
              Projet en cours de développement
            </span>
          </div>

          <h1 className="opacity-0 animate-fade-in-up animate-delay-100 font-cabinet text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-white leading-[1.15]">
            Votre jardin devrait être<br />
            <span className="text-zinc-400">un plaisir, pas une corvée.</span>
          </h1>

          <p className="opacity-0 animate-fade-in-up animate-delay-200 text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto">
            Nous développons un robot autonome pour que vous n'ayez plus jamais à ramasser les déjections de votre chien. Aidez-nous à créer la solution idéale.
          </p>

          <div className="opacity-0 animate-fade-in-up animate-delay-300 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-google-form-btn"
              className="group flex items-center gap-3 px-8 py-4 bg-white text-black font-medium rounded-full hover:bg-zinc-100 transition-all duration-300 hover:gap-4"
            >
              Donner mon avis (1 min)
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          <ProgressBar />
        </div>
      </div>
    </section>
  );
};

// Progress Bar Component
const ProgressBar = () => {
  const [progress, setProgress] = useState(0);
  const targetProgress = 84;

  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= targetProgress) {
            clearInterval(interval);
            return targetProgress;
          }
          return prev + 1;
        });
      }, 20);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="opacity-0 animate-fade-in-up animate-delay-400 max-w-md mx-auto space-y-3 pt-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-zinc-500">Objectif : 100 participants</span>
        <span className="text-sm font-semibold text-amber-500">{progress}/100</span>
      </div>
      <div
        data-testid="urgency-progress-bar"
        className="h-2 bg-white/10 rounded-full overflow-hidden"
      >
        <div
          className="h-full bg-amber-500 rounded-full progress-glow transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-zinc-600">Plus que {100 - progress} places pour participer à la phase de conception</p>
    </div>
  );
};

// Problem Section - The daily pain
const ProblemSection = () => {
  const painPoints = [
    {
      icon: Clock,
      stat: "10 min/jour",
      text: "perdues à ramasser",
    },
    {
      icon: Frown,
      stat: "365 fois/an",
      text: "cette corvée se répète",
    },
  ];

  return (
    <section data-testid="problem-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="uppercase text-xs tracking-[0.2em] text-zinc-500 font-semibold">
            Le problème
          </span>
          <h2 className="mt-6 font-cabinet text-3xl sm:text-4xl font-medium text-white leading-tight">
            Une corvée qu'on repousse.<br />
            <span className="text-zinc-500">Jusqu'à ce qu'on ne puisse plus.</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            Sous la pluie, dans le froid, ou en plein été. Ce moment désagréable qu'on évite jusqu'au dernier moment — et qui gâche le plaisir d'avoir un jardin.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {painPoints.map((point, index) => (
            <div
              key={index}
              data-testid={`pain-point-${index}`}
              className="card-hover p-8 bg-[#111111] rounded-3xl border border-white/10 text-center"
            >
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-2xl bg-red-500/10 border border-red-500/20">
                <point.icon size={24} className="text-red-400" />
              </div>
              <p className="mt-6 font-cabinet text-3xl font-medium text-white">
                {point.stat}
              </p>
              <p className="mt-2 text-zinc-500">
                {point.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Vision Section - The dream
const VisionSection = () => {
  return (
    <section data-testid="vision-section" className="py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1604404125020-4e020045c2d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGRvZyUyMGdhcmRlbnxlbnwwfHx8fDE3ODAwODMzOTh8MA&ixlib=rb-4.1.0&q=85"
              alt="Chien heureux dans un jardin propre"
              className="w-full h-80 lg:h-96 object-cover"
              data-testid="vision-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Text */}
          <div className="space-y-6">
            <span className="uppercase text-xs tracking-[0.2em] text-amber-500 font-semibold">
              Et si...
            </span>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-medium text-white leading-tight">
              Vous retrouviez le plaisir<br />
              <span className="text-zinc-400">de votre jardin.</span>
            </h2>
            <div className="space-y-4 text-zinc-400 leading-relaxed">
              <p>
                Imaginez sortir pieds nus dans l'herbe sans regarder où vous marchez.
              </p>
              <p>
                Laisser les enfants jouer librement. Profiter de votre extérieur comme il devrait l'être — un espace de détente, pas une liste de tâches.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Concept Section - The robot
const ConceptSection = () => {
  return (
    <section data-testid="concept-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text */}
          <div className="space-y-6 order-2 lg:order-1">
            <span className="uppercase text-xs tracking-[0.2em] text-zinc-500 font-semibold">
              Notre projet
            </span>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-medium text-white leading-tight">
              Un robot autonome<br />
              <span className="text-zinc-400">pensé pour votre jardin.</span>
            </h2>
            <div className="space-y-4 text-zinc-400 leading-relaxed">
              <p>
                Nous concevons une solution qui détecte et ramasse automatiquement — pour que vous n'ayez plus rien à faire.
              </p>
              <p>
                Discret, silencieux, et adapté aux espaces privés. Le produit est encore en phase de recherche, et nous avons besoin de votre avis pour le créer.
              </p>
            </div>
            
            <div className="flex items-center gap-3 pt-2">
              <Sparkles size={18} className="text-amber-500" />
              <span className="text-sm text-zinc-500">Design premium en cours de conception</span>
            </div>
          </div>

          {/* Concept Image */}
          <div className="relative rounded-3xl overflow-hidden border border-white/10 order-1 lg:order-2">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/8dbb1ee2-82c9-4a11-9596-21cee03de212/images/1b8d21fbda90617757121730b319395cefb3bb614b22316dcad58fa743dd2f0c.png"
              alt="Concept du robot PetConcept"
              className="w-full h-80 lg:h-96 object-cover blur-[2px] opacity-85 saturate-75"
              data-testid="concept-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/30" />
            <div className="absolute inset-0 concept-grain" />
            {/* Concept label */}
            <div className="absolute bottom-4 left-4 right-4">
              <span className="inline-block px-4 py-2 bg-black/70 backdrop-blur-sm rounded-full text-sm text-zinc-300 border border-white/10">
                Visuel concept — produit en développement
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer CTA Section
const FooterCTASection = () => {
  return (
    <section data-testid="footer-cta-section" className="relative py-32 md:py-40">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#111111] to-[#0a0a0a]" />

      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
        <span className="uppercase text-xs tracking-[0.2em] text-amber-500 font-semibold">
          Participez
        </span>
        <h2 className="mt-6 font-cabinet text-3xl sm:text-4xl lg:text-5xl font-medium text-white leading-tight">
          Aidez-nous à créer<br />
          <span className="text-zinc-400">la solution idéale.</span>
        </h2>
        <p className="mt-6 text-lg text-zinc-400 max-w-xl mx-auto">
          4 questions pour comprendre vos besoins. Votre avis façonnera directement le produit final.
        </p>
        
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-google-form-btn"
            className="group flex items-center gap-3 px-8 py-4 bg-white text-black font-medium rounded-full hover:bg-zinc-100 transition-all duration-300 hover:gap-4"
          >
            Répondre au questionnaire
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </a>
        </div>
        
        <p className="mt-6 text-sm text-zinc-600">
          Aucun engagement • 1 minute • 100% anonyme
        </p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-8 bg-[#050505] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="font-cabinet text-lg font-bold tracking-tight text-white">
            PETCONCEPT
          </span>
          <p className="text-sm text-zinc-600">
            © 2025 PetConcept • Projet en phase de recherche
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-[#050505] min-h-screen">
      <Navigation />
      <HeroSection />
      <ProblemSection />
      <VisionSection />
      <ConceptSection />
      <FooterCTASection />
      <Footer />
    </div>
  );
}

export default App;
